import eventMixin from "../lib/mixins/event";
import Logger from "../lib/logger";

export default class ThemeMobileNav {
	constructor(dom, loader) {
		const self = this;

		// Vars
		// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
		this.NAME = `ThemeMobileNav`;

		this.dom = dom;

		// MegaMenuLoader instance (lazy-loads submenu levels via the Section
		// Rendering API). Optional: without it, only server-rendered content shows.
		this.loader = loader || null;

		this.siteLogo = this.dom.querySelector(`.theme-header-main__logo`);

		this.mainHamburgerDom = this.dom.querySelector(
			`[data-theme-header-hamburger]`
		);

		this.mainCloseBtnDom = this.dom.querySelector(
			`[data-theme-header-close-btn]`
		);

		this.mobileNavDom = this.dom.querySelector(
			`[data-theme-header-mobile-nav]`
		);

		this.mobileNavBackBtn = this.dom.querySelector(
			`[data-mobile-nav-back-btn]`
		);

		this.currentMenu = this.dom.querySelector(`[data-mobile-nav-current-menu]`);

		this.menusWrapper = this.dom.querySelector(
			`[data-mobile-nav-menus-wrapper]`
		);

		this.menus = this.dom.querySelector(`[data-mobile-nav-menus]`);

		this.menusWidth = this.menusWrapper.getBoundingClientRect().width;
		this.navPos = 0;
		this.breadCrumbs = [];
	}

	// Logic
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	init() {
		Logger.info(`${this.NAME} > init`);
		this.initMainHamburger();
		this.initMainCloseBtn();

		window.addEventListener("resize", this.onResize.bind(this));

		this.initBackBtn();
		this.initMenuItems();
	}

	/**
	 * onResize
	 */
	onResize() {
		this.menusWidth = this.menusWrapper.getBoundingClientRect().width;
	}

	/**
	 * initMainHamburger - Initialize the main hamburger button
	 */
	initMainHamburger() {
		Logger.info(`${this.NAME} > initMainHamburger`);

		const self = this;

		this.mainHamburgerDom?.addEventListener("click", (e) => {
			e.preventDefault();
			self.showMobileNav();
		});
	}

	/**
	 * initMainCloseBtn - Initialize the main hamburger button
	 */
	initMainCloseBtn() {
		Logger.info(`${this.NAME} > initMainCloseBtn`);

		const self = this;

		this.mainCloseBtnDom?.addEventListener("click", (e) => {
			e.preventDefault();
			self.hideMobileNav();
		});
	}

	/**
	 * showMobileNav - Show the mobile nav
	 */
	showMobileNav() {
		Logger.info(`${this.NAME} > showMobileNav`);
		this.mainHamburgerDom.classList.add("menu-open");
		this.mainCloseBtnDom.classList.add("menu-open");
		this.mobileNavDom.classList.add("open");

		// When the mobile nav is show, we need to fix the body
		document.body.style.top = `-${window.scrollY}px`;
		// document.body.style.paddingRight = '15px';
		document.body.style.position = "fixed";
		document.body.style.width = "100%";
		document.body.style.zIndex = "0";

		document.body.addEventListener("keydown", this.handleEscKey.bind(this));
	}

	/**
	 * hideMobileNav - Hide the mobile nav
	 */
	hideMobileNav() {
		Logger.info(`${this.NAME} > hideMobileNav`);
		this.mainHamburgerDom.classList.remove("menu-open");
		this.mainCloseBtnDom.classList.remove("menu-open");
		this.mobileNavDom.classList.remove("open");
		this.menus.classList.remove("level-" + this.navPos.toString());
		// re-hide any sub-levels that were previously shown
		const submenus = this.menus.querySelectorAll(
			`.mobile-nav__submenu.visible`
		);
		submenus.forEach((submenu) => {
			submenu.classList.remove("visible");
		});
		this.navPos = 0;
		this.breadCrumbs = [];
		this.mobileNavBackBtn.classList.remove("visible");
		this.currentMenu.innerHTML = "";
		this.siteLogo.classList.remove("hide-mobile");

		// Allow the body to scroll again
		const scrollY = document.body.style.top;
		document.body.style.paddingRight = "";
		document.body.style.position = "";
		document.body.style.width = "";
		document.body.style.top = "";
		document.body.style.zIndex = "";
		window.scrollTo(0, parseInt(scrollY || "0") * -1);

		document.body.removeEventListener("keydown", this.handleEscKey.bind(this));
	}

	/**
	 * handleEscKey - Enable the escape key to close the meganav
	 */
	handleEscKey(e) {
		Logger.info(`${this.NAME} > handleEscKey`);
		if (e.key === "Escape") {
			this.hideMobileNav();
		}
	}

	/**
	 * initMenuItems
	 *
	 * Uses event delegation on the menus container (instead of binding each
	 * item at init) because submenu levels are injected lazily via the
	 * Section Rendering API after page load.
	 */
	initMenuItems() {
		Logger.info(`${this.NAME} > initMenuItems`);

		const self = this;
		const itemSelector = [
			`[data-menu-item]`,
			`[data-submenu-1-item]`,
			`[data-submenu-2-item]`,
			`[data-submenu-3-item]`,
			`[data-submenu-4-item]`,
		].join(`,`);

		this.menus.addEventListener("click", (e) => {
			if (!(e.target instanceof Element)) return;

			const item = e.target.closest(itemSelector);
			if (!item || !self.menus.contains(item)) return;

			// Let real links navigate (leaf items render <a> titles)
			const anchor = e.target.closest("a[href]");
			if (anchor && item.contains(anchor)) return;

			let level = 0;
			if (item.hasAttribute("data-submenu-1-item")) level = 1;
			else if (item.hasAttribute("data-submenu-2-item")) level = 2;
			else if (item.hasAttribute("data-submenu-3-item")) level = 3;
			else if (item.hasAttribute("data-submenu-4-item")) level = 4;

			const submenu = item.querySelector(`[data-submenu-${level + 1}]`);
			if (!submenu) return;

			// Lazy-load this level's content via the Section Rendering API
			// (usually an instant cache hit thanks to eager warming), and warm
			// the next level's product caches so the following tap is instant.
			if (self.loader) {
				if (submenu.hasAttribute("data-menu-lazy-panel")) {
					self.loader.loadPanel(submenu);
				} else if (submenu.hasAttribute("data-menu-lazy-products")) {
					self.loader.loadProducts(submenu);
				}
				self.loader.warmNextLevelProducts(submenu);
			}

			submenu.classList.add("visible");

			const titleDom = item.querySelector(
				".menu-item__title, .submenu-item__title"
			);
			const title = titleDom ? titleDom.textContent.trim() : "";
			self.updateNavPosition(title, level + 1);
		});
	}

	/**
	 * initBackBtn
	 */
	initBackBtn() {
		Logger.info(`${this.NAME} > initBackBtn`);

		this.mobileNavBackBtn.addEventListener("click", () => {
			this.menus.classList.remove("level-" + this.navPos.toString());

			// re-hide any sub-levels that were previously shown
			const submenus = this.menus.querySelectorAll(
				`[data-submenu-${this.navPos.toString()}]`
			);
			setTimeout(() => {
				submenus.forEach((submenu) => {
					submenu.classList.remove("visible");
				});
			}, 500);

			this.navPos--;

			this.breadCrumbs.pop();
			this.currentMenu.innerHTML = this.breadCrumbs[this.navPos - 1];

			this.menus.classList.add("level-" + this.navPos.toString());

			if (this.navPos == 0) {
				this.mobileNavBackBtn.classList.remove("visible");
				this.currentMenu.innerHTML = "";
				this.siteLogo.classList.remove("hide-mobile");
			}

			// update scroll position of menus wrapper
			this.menusWrapper.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		});
	}

	/**
	 * updateNavPosition
	 */
	updateNavPosition(title, new_position) {
		Logger.info(`${this.NAME} > updateNavPosition ${new_position}`);

		// update position of menus
		this.menus.classList.remove("level-" + this.navPos.toString());
		this.navPos = new_position;
		this.menus.classList.add("level-" + this.navPos.toString());

		// update current menu
		this.breadCrumbs.push(title);
		this.currentMenu.innerHTML = this.breadCrumbs[this.navPos - 1];

		// update back button
		if (this.navPos > 0) {
			this.mobileNavBackBtn.classList.add("visible");
			this.siteLogo.classList.add("hide-mobile");
		} else {
			this.mobileNavBackBtn.classList.remove("visible");
			this.siteLogo.classList.remove("hide-mobile");
		}

		// update scroll position of menus wrapper
		this.menusWrapper.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}
}

// Mixins
Object.assign(ThemeMobileNav.prototype, eventMixin);
